/** @jsxRuntime classic */
/** @jsx jsx */
import { jsx } from "@emotion/react";
import { LoaderLayout } from "components/atoms/loader/LoaderLayout";
import { format } from "date-fns";
import { useI18n } from "hooks/i18n/i18n";
import { modalActions } from "modules/modal";
import { useEffect } from "react";
import { useDispatch } from "react-redux";
import { BreadcrumbNavigation } from "ui/BreadcrumbNavigation";
import { SubContentsLayout } from "ui/SubContentsLayout";

import { DownloadItem } from "./components/DownloadItem";
import { containerStyle, listContainerStyle, titleStyle } from "./styles";

import type { EventTicketBonusMedia } from "hooks/event/useEventTicketsBonusMediaLazy";
import type { FC } from "react";
import type { Options } from "utility/isFeatureEnabled/types/options";

type Props = {
  orderId: string;
  lineItemId: string;
  bonusMediaList: EventTicketBonusMedia[];
  isLoading: boolean;
  error: Error | null;
};

const formatDateString = (date: Date | undefined): string | undefined => {
  if (!date) return undefined;
  return format(date, "yyyy/MM/dd HH:mm");
};

const FEATURE_FLAG: Options["featureName"] = "newPurchaseHistory";

export const View: FC<Props> = (props) => {
  const { orderId, bonusMediaList, isLoading, error } = props;

  const { t } = useI18n();
  const dispatch = useDispatch();

  useEffect(() => {
    if (error) {
      dispatch(
        modalActions.toggleActionModal({
          actionModalType: "confirmAction",
          caption: t("settlementHistory.errorCaption"),
          msg: t("settlementHistory.errorMessage"),
          btnMsg: t("settlementHistory.errorBackToOrderDetail"),
          action: () => {
            window.location.href = `/account/settlement/history/${orderId}`;
          },
          args: undefined,
        })
      );
    }
  }, [error, dispatch, orderId, t]);

  if (isLoading) {
    return (
      <SubContentsLayout>
        <LoaderLayout />
      </SubContentsLayout>
    );
  }

  return (
    <SubContentsLayout bgcolor="GRAY">
      <BreadcrumbNavigation
        breadcrumbs={[
          { label: t("common.routes.home"), link: "/" },
          {
            label: t("common.routes.settlementHistory"),
            link: "/account/settlement/history",
          },
          {
            label: t("settlementHistory.pageTitle"),
            link: `/account/settlement/history/${orderId}?${FEATURE_FLAG}`,
          },
          {
            label: t("settlementHistory.digitalBonusDownload"),
            link: null,
          },
        ]}
      />
      <div css={containerStyle}>
        <div css={listContainerStyle}>
          <h1 css={titleStyle}>
            {t("settlementHistory.digitalBonusDownloadTitle")}
          </h1>
          {bonusMediaList.map((bonus) => (
            <DownloadItem
              key={bonus.mediaId}
              title={bonus.bonusName}
              startDate={formatDateString(bonus.releaseAt)}
              expirationDate={formatDateString(bonus.expiredAt)}
              downloadUrl={bonus.urlForDownload}
            />
          ))}
        </div>
      </div>
    </SubContentsLayout>
  );
};
